import { useMemo } from "react";
import { useApi, apiResolver } from "@/api/useApi";
import {
  Release,
  ReleaseStatus,
  UpsertReleaseConversationRequest,
  UpsertReleaseConversationResponse,
  UpsertReleaseParamsDto,
  Version,
} from "./Release.model";
import { ReleaseConversation } from "../release-conversation/ReleaseConversation.model";
import { Skill } from "@/v2/entities/skill/Skill.model";
import { useQuery } from "react-query";

type GetReleaseSkillsResponse = {
  skills: Skill[];
};

type UpsertSkillToReleasePayload = {
  skillId: string;
  maxUsage?: number;
};

type RemoveSkillFromReleasePayload = {
  skillId: string;
};

export function useReleaseApi() {
  const { api } = useApi();

  return useMemo(
    () => ({
      getReleases: async (status?: ReleaseStatus) => {
        const params = new URLSearchParams();
        if (status) params.append("status", status);

        return apiResolver<Release[]>(
          api.get(
            `/platform/releases${
              params.toString() ? `?${params.toString()}` : ""
            }`
          )
        );
      },
      getRelease: async (
        releaseId: string,
        includeTopicReleasePolicies?: boolean,
        includeConversations?: boolean
      ) => {
        const params = new URLSearchParams();
        if (includeTopicReleasePolicies)
          params.append("includeTopicReleasePolicies", "true");
        if (includeConversations) params.append("includeConversations", "true");

        return apiResolver<Version>(
          api.get(
            `/platform/releases/${releaseId}${
              params.toString() ? `?${params.toString()}` : ""
            }`
          )
        );
      },
      updateRelease: async (releaseId: string, input: UpsertReleaseParamsDto) =>
        apiResolver<Release | null>(
          api.put(`/platform/releases/${releaseId}`, input)
        ),
      getReleaseConversations: async (
        releaseId: string,
        includeMessages?: boolean
      ) => {
        const params = new URLSearchParams();
        if (includeMessages) params.append("includeMessages", "true");

        return apiResolver<ReleaseConversation[]>(
          api.get(
            `/platform/releases/${releaseId}/conversations${
              params.toString() ? `?${params.toString()}` : ""
            }`
          )
        );
      },
      upsertConversation: async ({
        releaseId,
        ...data
      }: UpsertReleaseConversationRequest) =>
        apiResolver<UpsertReleaseConversationResponse>(
          api.post(`/platform/releases/${releaseId}/conversation`, data)
        ),
      getReleaseSkills: async (releaseId: string) =>
        apiResolver<GetReleaseSkillsResponse>(
          api.get(`/platform/releases/${releaseId}/skills`)
        ),
      upsertSkillToRelease: async (
        releaseId: string,
        payload: UpsertSkillToReleasePayload
      ) =>
        apiResolver<{ success: boolean }>(
          api.post(`/platform/releases/${releaseId}/skills`, payload)
        ),
      removeSkillFromRelease: async (
        releaseId: string,
        payload: RemoveSkillFromReleasePayload
      ) =>
        apiResolver<{ success: boolean }>(
          api.delete(`/platform/releases/${releaseId}/skills`, {
            data: payload,
          })
        ),
      checkTicketReleaseExists: async (ticketId: string) =>
        apiResolver<boolean>(api.get(`/platform/releases/isExist/${ticketId}`)),
      getSimilarTickets: async (topicId: string) =>
        apiResolver<any[]>(
          api.get(`/platform/releases/${topicId}/similar-tickets`)
        ),
    }),
    [api]
  );
}

export function useGetSkillsForRelease(releaseId: number | undefined | null) {
  const { getReleaseSkills } = useReleaseApi();

  return useQuery({
    queryKey: ["release", releaseId, "skills"],
    queryFn: () => getReleaseSkills(releaseId?.toString() ?? ""),
    enabled: releaseId != null,
  });
}
